import { z } from "zod";
import { flowStepSchema } from "./flows";

export const podConfigSchema = z.object({
  image: z.string().trim().nullable().optional(),
  cpu: z.string().trim().nullable().optional(),
  memory: z.string().trim().nullable().optional(),
  command: z.string().trim().nullable().optional(),
  parallel: z.number().int().min(1).max(32).optional(),
});
export type PodConfig = z.infer<typeof podConfigSchema>;

export const draftStepSchema = flowStepSchema.extend({
  id: z.string().min(1),
  position: z.number().int().min(1),
  rejectRoute: z.string().nullable(),
  deciders: z.array(z.string().min(1)),
  expiryMinutes: z.number().int().min(1).nullable(),
  toolRef: z.string().nullable(),
  /** Project skill-install id bound to this step, or null when no skill is selected. */
  skillRef: z.string().nullable().optional(),
  spendCap: z.number().int().min(0).nullable().optional(),
  podConfig: podConfigSchema.optional(),
  dependsOn: z.array(z.string()).optional(),
});
export type DraftStep = z.infer<typeof draftStepSchema>;

/** A step kind deploys when it names a deploy, the same word the readiness rule uses. */
export function kindDeploys(kind: string): boolean {
  return kind.toLowerCase().includes("deploy");
}

/** True when any step deploys, so publishing needs an approver's confirmation first. */
export function workflowDeploys(steps: readonly { kind: string }[]): boolean {
  return steps.some((step) => kindDeploys(step.kind));
}

/** Gates, planning, and notes do not call an external system; other steps need a bound tool. */
export function stepNeedsTool(step: Pick<DraftStep, "gate" | "kind">): boolean {
  if (step.gate) return false;
  return !["planner", "approval", "note"].includes(step.kind.toLowerCase());
}

export const flowDraftSchema = z.object({
  flowId: z.uuid(),
  version: z.number().int().min(1),
  steps: z.array(draftStepSchema),
  workflowCap: z.number().int().min(1).nullable().optional(),
  updatedAt: z.iso.datetime(),
});
export type FlowDraft = z.infer<typeof flowDraftSchema>;

export const saveFlowDraftSchema = z.object({
  steps: z.array(draftStepSchema).min(1),
  workflowCap: z.number().int().min(1).nullable().optional(),
});
export type SaveFlowDraft = z.infer<typeof saveFlowDraftSchema>;

/**
 * The body that publishes a draft. A workflow that deploys names the approval that confirmed it;
 * a workflow that does not deploy publishes without one.
 */
export const publishDraftSchema = saveFlowDraftSchema.extend({
  approvalId: z.uuid().nullable().optional(),
});
export type PublishDraft = z.infer<typeof publishDraftSchema>;

export const flowCostEstimateSchema = z.object({
  minCredits: z.number().int().min(0),
  maxCredits: z.number().int().min(0),
  workflowCap: z.number().int().min(0),
  reservedCap: z.number().int().min(0),
  isLowCap: z.boolean(),
  warning: z.string().nullable().optional(),
});
export type FlowCostEstimate = z.infer<typeof flowCostEstimateSchema>;

/**
 * Calculates the estimated cost range and reserved cap for a workflow draft.
 * - The reserved cap matches the upfront reservation taken at run start.
 * - The estimated range reflects lower-bound expected usage up to the step caps or workflow cap.
 * - A low-cap warning is triggered when the workflow cap is lower than the sum of step caps.
 */
export function calculateFlowCostEstimate(
  steps: DraftStep[],
  workflowCap?: number | null,
  historyAvgCredits?: number | null,
): FlowCostEstimate {
  let sumStepCaps = 0;
  for (const step of steps) {
    if (typeof step.spendCap === "number" && step.spendCap > 0) {
      sumStepCaps += step.spendCap;
    } else if (step.kind === "agent" || step.recipe?.startsWith("agent.")) {
      sumStepCaps += 500;
    }
  }

  const effectiveCap =
    typeof workflowCap === "number" && workflowCap > 0 ? workflowCap : Math.max(sumStepCaps, 1000);

  const minCredits =
    typeof historyAvgCredits === "number" && historyAvgCredits > 0
      ? Math.min(historyAvgCredits, effectiveCap)
      : Math.max(100, Math.round(effectiveCap * 0.25));

  const maxCredits = Math.min(sumStepCaps > 0 ? sumStepCaps : effectiveCap, effectiveCap);

  const isLowCap = typeof workflowCap === "number" && workflowCap > 0 && workflowCap < sumStepCaps;

  const warning = isLowCap
    ? `Workflow cap (${workflowCap} credits) is lower than the sum of step caps (${sumStepCaps} credits).`
    : null;

  return {
    minCredits,
    maxCredits,
    workflowCap: effectiveCap,
    reservedCap: effectiveCap,
    isLowCap,
    warning,
  };
}

export const validationIssueSchema = z.object({
  stepId: z.string().min(1),
  rule: z.enum([
    "gate-before-deploy",
    "reject-route",
    "no-write-credential",
    "tool-bound",
    "skill-bound",
    "recipe-known",
    "capability-approved",
    "agent-spend-cap",
    "workflow-cap",
    "author-not-decider",
  ]),
  message: z.string().min(1),
});
export type ValidationIssue = z.infer<typeof validationIssueSchema>;

export const validateDraftResponseSchema = z.object({
  valid: z.boolean(),
  issues: z.array(validationIssueSchema),
});
export type ValidateDraftResponse = z.infer<typeof validateDraftResponseSchema>;
